import {FlowBlock, FlowProperty, FlowWire} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {VisualStreamIds} from '@mintdata-wsp/common/state/ui/visualDesigner/visualData/VisualStreamIds';
import {Perspectives, ScreenRegion} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/layout/Perspective';
import {
    IPanelDatum,
    PanelHeader,
    PanelKind,
    PanelTitleFn
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/PanelState';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {IWSPIconProps, WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {SelectedBlockChangedEvent} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/flow/FlowEventsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {PropertyValueChangeFn} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/widgets/FlowPropertiesWidgets';
import {
    DebugBlockContext,
    DebugDataPanel,
    DebugDataPanelProps,
    DebugKind,
    DebugWireContext
} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/debugData/DebugDataPanel';
import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';
import bind from 'bind-decorator';
import {action, IArraySplice, IReactionDisposer, observe, reaction} from 'mobx';
import * as React from 'react';

export class FlowDataPanelsController
{
    public init():void
    {
        this._proactivelyLoadMonacoInIframe();

        this._listenForBlockSelection();
        this._listenForWireSelection();

        // this._listenForBlockDeletion();
        // this._listenToPanelSelection();
    }

    public dispose():void
    {
        this._reactionDisposers.forEach(disposer => disposer());
        this._eventDisposers.forEach(({dispose}) => dispose());
    }

    private _reactionDisposers:IReactionDisposer[] = [];
    private _eventDisposers:IDisposable[] = [];

    @bind
    private _listenForBlockSelection():void
    {
        this._onBlockSelectionChanged();

        const disposer = controllers.ui.flowEvents.addListener(this._onBlockSelectionChanged);

        this._eventDisposers.push(disposer);
    }

    @bind
    private _listenForWireSelection():void
    {
        this._onWireSelectionChanged();

        const disposer = reaction(
            () => (controllers.ui.flowSelection.getSelectedWire()),
            () => this._onWireSelectionChanged()
        );

        this._reactionDisposers.push(disposer);
    }

    @bind
    private _onBlockSelectionChanged(event?:SelectedBlockChangedEvent)
    {
        const {visualData, flowSelection, perspective, panels} = controllers.ui;

        if( event && !event.manually )
        {
            return;
        }

        const selectedBlock = flowSelection.getAnySingleSelectedBlock();

        if( selectedBlock )
        {
            const streams = VisualStreamIds.forBlock(selectedBlock);

            visualData.getOrCreateStreamer(streams.input);
            visualData.getOrCreateStreamer(streams.output);

            // this._ensureDebugBlockPanel();
        }
        else
        {
            const flowTab = perspective.getPerspective(Perspectives.FLOW_TAB);
            panels.general.removeAnyUnpinnedOfKind(flowTab, PanelKind.FlowDebugData);
        }
    }

    private _ensureDebugBlockPanel()
    {
        const selectedBlock = controllers.ui.flowSelection.getAnySingleSelectedBlock();
        const controller = controllers.ui.panels.general;
        const flowTab = controllers.ui.perspective.getPerspective(Perspectives.FLOW_TAB);

        controller.removeAnyUnpinnedOfKind(flowTab, PanelKind.FlowDebugData);

        if( !selectedBlock )
            return;

        if( selectedBlock.isDebugBlock )
            return;

        // if( selectedBlock.inputPorts.length < 1 || selectedBlock.outputPorts.length < 1 )
        //     return;

        const vdController = controllers.ui.visualData;
        const blockStreams = VisualStreamIds.forBlock(selectedBlock);

        const inputPort = selectedBlock.inputPorts.length > 0 ? selectedBlock.inputPorts[0] : null;
        const outputPort = selectedBlock.outputPorts.length > 0 ? selectedBlock.outputPorts[0] : null;
        const inputStreamer = blockStreams.input ? vdController.getStreamer(blockStreams.input) : null;
        const outputStreamer = blockStreams.output ? vdController.getStreamer(blockStreams.output) : null;

        // let timeline = vdController.getOrCreateTimeline((blockStreams.output as VisualStreamIdSet).getStreamIds()[0]);
        // let numFrameEntries = timeline.getFrameEntries().length;
        // let gridModelNumRows = outputStreamer.getGridModel().getBody().getNumRows();
        // console.log(`###################### numFrameEntries: ${numFrameEntries} gridModelNumRows: ${gridModelNumRows}`);

        const panelHeader = this._createBlockPanelHeader(selectedBlock);

        const debugBlockContext = new DebugBlockContext(selectedBlock, inputPort, outputPort, inputStreamer, outputStreamer);
        const props:DebugDataPanelProps =
            {
                kind: DebugKind.Block,
                panelTitle: panelHeader.title as PanelTitleFn,
                context: debugBlockContext
            };

        const element = React.createElement(DebugDataPanel, props);

        const dataDebugPanel = controller.getOrCreatePanel(flowTab,
            ScreenRegion.South,
            PanelKind.FlowDebugData,
            panelHeader,
            debugBlockContext,
            element);

        controller.selectPanel(dataDebugPanel.id);
    }

    @bind
    private _onWireSelectionChanged():void
    {
        const {flowSelection, panels, perspective} = controllers.ui;

        const selectedWire = flowSelection.getSelectedWire();

        if( selectedWire )
        {
            this._ensureDebugWirePanel();
        }
        else
        {
            const flowTab = perspective.getPerspective(Perspectives.FLOW_TAB);
            panels.general.removeAnyUnpinnedOfKind(flowTab, PanelKind.FlowDebugData);
        }

    }

    private _ensureDebugWirePanel()
    {
        const selectedWire = controllers.ui.flowSelection.getSelectedWire();
        const controller = controllers.ui.panels.general;
        const flowTab = controllers.ui.perspective.getPerspective(Perspectives.FLOW_TAB);

        controller.removeAnyUnpinnedOfKind(flowTab, PanelKind.FlowDebugData);

        if( !selectedWire )
            return;

        const visualStreamId = VisualStreamIds.forWire(selectedWire);
        if( !visualStreamId )
        {
            console.warn('Warning: cannot compute visualStreamId for selectedWire: ', selectedWire);
            return;
        }

        const streamer = controllers.ui.visualData.getOrCreateStreamer(visualStreamId);

        const panelHeader = this._createWirePanelHeader(selectedWire);

        const debugWireContext = new DebugWireContext(selectedWire, streamer);
        const props:DebugDataPanelProps =
            {
                kind: DebugKind.Wire,
                panelTitle: panelHeader.title as PanelTitleFn,
                context: debugWireContext
            };

        const element = React.createElement(DebugDataPanel, props);

        const dataDebugPanel = controller.getOrCreatePanel(flowTab,
            ScreenRegion.South,
            PanelKind.FlowDebugData,
            panelHeader,
            debugWireContext,
            element);

        controller.selectPanel(dataDebugPanel.id);
    }

    @bind
    private _listenForBlockDeletion():void
    {
        const disposer = reaction(
            () => (controllers.ui.flow.getOpenFlow()),
            () =>
            {
                const blocks = controllers.ui.flow.getOpenFlow().flowBlocks;

                observe(blocks, (change:IArraySplice<FlowBlock> | any) =>
                {
                    if( change.removed )
                    {
                        for( let removedBlock of change.removed )
                            this._onBlockRemoved(removedBlock);
                    }
                });
            }
        );

        this._reactionDisposers.push(disposer);
    }

    private _createWirePanelHeader(selectedWire:FlowWire):PanelHeader
    {
        const panelTitleFn = PanelHeader.newPanelTitleFn(() =>
        {
            const fromBlock = NameUtil.getInstanceOrBlockName(selectedWire.source.block);
            const toBlock = NameUtil.getInstanceOrBlockName(selectedWire.target.block);

            return `${fromBlock} ➡ ${toBlock}`;
        });

        const panelHeader = PanelHeader.withCloseableTitle(panelTitleFn);
        panelHeader.icon = WSPIcon.forHeader(WSPIcons.visualDesigner.panels.headers.DebugDataWire);

        return panelHeader;
    }

    public static newIcon(iconId:string):React.ReactElement<IWSPIconProps>
    {
        return WSPIcon.for(iconId, WSPIconSize.medium18);
    }

    private _createBlockPanelHeader(block:FlowBlock):PanelHeader
    {
        const panelTitleFn = PanelHeader.newPanelTitleFn(() =>
        {
            const title = NameUtil.getInstanceOrBlockName(block);
            return title;
        });

        const panelHeader = PanelHeader.withCloseableTitle(panelTitleFn);
        panelHeader.icon = WSPIcon.forHeader(WSPIcons.visualDesigner.panels.headers.DebugDataBlock2);

        return panelHeader;
    }

    @bind
    private _proactivelyLoadMonacoInIframe():void
    {
        const flowTab = controllers.ui.perspective.getPerspective(Perspectives.FLOW_TAB);
        const southPanelGroup = flowTab.getPanelGroupForRegion(ScreenRegion.South);

        const controller = controllers.ui.panels.codeEditor;
        controller.ensureCodeEditorAvailableIn(southPanelGroup);
    }

    @bind
    private _listenToPanelSelection()
    {
        this._onSelectedPanelChanged();

        const disposer = reaction(
            () => (this._getDataPanelGroup().selectedPanel),
            () => this._onSelectedPanelChanged()
        );

        this._reactionDisposers.push(disposer);
    }

    @bind
    private _onSelectedPanelChanged()
    {
        const panelGroup = this._getDataPanelGroup();
        const selectedPanel = panelGroup.selectedPanel;

        if( selectedPanel )
        {
            const datum = selectedPanel.datum;

            if( datum instanceof DebugBlockContext )
            {
                const blockCtx = datum as DebugBlockContext;
                controllers.ui.flowSelection.setSingleBlockSelection(blockCtx.block);
            }
        }
    }

    @bind
    private _getDataPanelGroup()
    {
        const controller = controllers.ui.perspective;
        const perspective = controller.getPerspective(Perspectives.FLOW_TAB);

        return perspective.getPanelGroup(ScreenRegion.South);
    }

    @bind
    private _onBlockRemoved(block:FlowBlock)
    {
        const scriptProp = this._getSingleScriptProperty(block);
        if( !scriptProp )
            return;

        const datum = new CodePanelDatum(block, scriptProp);
        controllers.ui.panels.general.removePanelByDatum(datum);
    }

    @bind
    private _getSingleScriptProperty(block:FlowBlock):FlowProperty | null
    {
        let numScriptProperties = 0;
        let singleScriptProperty:FlowProperty = null;

        for( let prop of block.properties )
        {
            if( prop.definition.isJavascript )
            {
                if( numScriptProperties === 0 )
                    singleScriptProperty = prop;
                else
                    singleScriptProperty = null;

                numScriptProperties++;
            }
        }

        return singleScriptProperty;
    }

    @bind
    public activateCodeEditorFor(block:FlowBlock, prop:FlowProperty, onChange:PropertyValueChangeFn):void
    {
        const datum = new CodePanelDatum(block, prop);
        const panelTitleFn = PanelHeader.newPanelTitleFn(() => (this._blockToTitle(block)));

        const perspective = controllers.ui.perspective.getPerspective(Perspectives.FLOW_TAB);
        const element = React.createElement('div', {});
        const controller = controllers.ui.panels.general;
        const panelHeader = PanelHeader.withCloseableTitle(panelTitleFn);
        panelHeader.icon = WSPIcon.forHeader(WSPIcons.visualDesigner.panels.headers.Script);

        const panel = controller.getOrCreatePanel(perspective,
            ScreenRegion.South,
            PanelKind.FlowCodeEditor,
            panelHeader,
            datum,
            element);

        // panel.datum = block as IPanelDatum;

        const codeEditor = controllers.ui.panels.codeEditor;
        const content = prop.value ? prop.value : "";

        codeEditor.setContent(panel, content, action((newContent:string) =>
        {
            // let title = (panel.header.title as any)();
            // console.log(`newContent ${title}: ${newContent}`);

            // const dataSyncPath = `flow.codeEditors.${panelId}`;
            // controllers.net.dataSync.sendAssignPatch(dataSyncPath, newContent);

            onChange(newContent);
        }));

        controllers.ui.panels.general.selectPanel(panel.id);
    }

    @bind
    private _blockToTitle(block:FlowBlock):string
    {
        return block.instanceName ?
            block.instanceName :
            NameUtil.getDisplayName(block.component);
    }
}

export class CodePanelDatum implements IPanelDatum
{
    public readonly datumType = 'CodePanelDatum';

    constructor(public readonly block:FlowBlock,
                public readonly prop:FlowProperty)
    {
    }

    isEqualTo(other:CodePanelDatum):boolean
    {
        if( !other )
            return false;

        return other.datumType === this.datumType &&
            other.block && other.block.isEqualTo(this.block) &&
            other.prop && other.prop.isEqualTo(this.prop);
    }
}
